import { PropsWithoutRef, useEffect, useMemo } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SummarizeTwoToneIcon from "@mui/icons-material/SummarizeTwoTone";
import CloseIcon from "@mui/icons-material/Close";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { SIDEBAR_WIDTH } from "../constants";
import QuizIcon from "@mui/icons-material/Quiz";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import SavingsIcon from "@mui/icons-material/Savings";
import PaymentIcon from "@mui/icons-material/Payment";
import BarChartIcon from "@mui/icons-material/BarChart";
import LabelIcon from "@mui/icons-material/Label";
import CategoryIcon from "@mui/icons-material/Category";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import PhotoCameraFrontIcon from "@mui/icons-material/PhotoCameraFront";
import useAuth from "../hooks/useAuth";
import MapIcon from "@mui/icons-material/Map";
import useTranslation from "../hooks/Translation";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import Logo from "../assets/logonew.jpg";
import { PERMISSION_MODULES } from "../constants/permissionModules";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const location = useLocation();
  const userData = useAuth();
  const navigate = useNavigate();
  const translate = useTranslation();

  const items = useMemo(
    () => [
    {
      name: translate?.sideBar?.dashboard,
      module: PERMISSION_MODULES.DASHBOARD,
      icon: <DashboardIcon />,
      key: "1",
      selected: "/dashboard",
    },
    {
      name: translate?.sideBar?.user,
      module: PERMISSION_MODULES.MANAGE_USERS,
      icon: <PersonAddIcon />,
      key: "2",
      selected: "/manageusers",
    },
    {
      name: translate?.sideBar?.agent,
      module: PERMISSION_MODULES.MANAGE_AGENTS,
      icon: <PeopleIcon />,
      key: "3",
      selected: "/manage-agents",
    },
    {
      name: translate?.sideBar?.subAdmin,
      module: PERMISSION_MODULES.MANAGE_SUB_ADMIN,
      icon: <PeopleIcon />,
      key: "4",
      selected: "/manage-subAdmin",
    },
    {
      name: translate?.sideBar?.plans,
      module: PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS,
      icon: <SubscriptionsIcon />,
      key: "5",
      selected: "/manage-subscription",
    },
    {
      name: translate?.sideBar?.promotionalCode,
      module: PERMISSION_MODULES.MANAGE_PROMOTIONAL_CODE,
      icon: <LocalOfferIcon />,
      key: "6",
      selected: "/manage-discount",
    },
    {
      name: translate?.sideBar?.category,
      module: PERMISSION_MODULES.MANAGE_CATEGORIES,
      icon: <CategoryIcon />,
      key: "7",
      selected: "/manage-categories",
    },
    {
      name: translate?.sideBar?.property,
      module: PERMISSION_MODULES.MANAGE_PROPERTIES,
      icon: <MapsHomeWorkIcon />,
      key: "8",
      selected: "/manage-properties",
    },
    {
      name: translate?.sideBar?.areas,
      module: PERMISSION_MODULES.MANAGE_AREAS,
      icon: <MapIcon />,
      key: "9",
      selected: "/manage-areas",
    },
    {
      name: translate?.sideBar?.tutorial,
      module: PERMISSION_MODULES.PROMOTIONAL_TUTORIALS,
      icon: <PhotoCameraFrontIcon />,
      key: "10",
      selected: "/manage-tutorials",
    },
    {
      name: translate?.sideBar?.payments,
      module: PERMISSION_MODULES.MANAGE_PAYMENTS,
      icon: <PaymentIcon />,
      key: "11",
      selected: "/manage-payments",
    },
    {
      name: translate?.sideBar?.refundRequests,
      module: PERMISSION_MODULES.REFUND_REQUESTS,
      icon: <CurrencyExchangeIcon />,
      key: "12",
      selected: "/refund-requests",
    },
    {
      name: translate?.sideBar?.notifications,
      module: PERMISSION_MODULES.MANAGE_NOTIFICATIONS,
      icon: <NotificationsActiveIcon />,
      key: "13",
      selected: "/manage-notifications",
    },

    {
      name: translate?.sideBar?.analytics,
      module: PERMISSION_MODULES.ANALYTICS,
      icon: <BarChartIcon />,
      key: "14",
      selected: "/analytics",
    },
    {
      name: translate?.sideBar?.setting,
      module: PERMISSION_MODULES.SETTINGS,
      icon: <SummarizeTwoToneIcon />,
      key: "15",
      selected: "/cms",
    },
    {
      name: translate?.sideBar?.faqs,
      module: PERMISSION_MODULES.FAQS,
      icon: <BarChartIcon />,
      key: "16",
      selected: "/faq",
    },
    ],
    [translate]
  );

  const list = useMemo(() => {
    if (userData?.role === "ADMIN") {
      return items;
    }
    if (!userData?.permission?.length) {
      return [];
    }
    return items.filter((menuItem) => {
      const perm = userData.permission.find(
        (p: { name?: string; isView?: boolean }) => p?.name === menuItem.module
      );
      return !!perm?.isView;
    });
  }, [userData, items]);

  const handleNavigate = () => {
    if (list.find((item: any) => item?.name !== "Dashboard")) {
      navigate("/dashboard");
    }
  };

  useEffect(() => {
    const sidebars = document.querySelectorAll('.sidebar_drawer .MuiDrawer-paper');
    const scrollPos = sessionStorage.getItem('sidebarScrollPos');
    if (scrollPos) {
      sidebars.forEach((sidebar) => {
        sidebar.scrollTop = parseInt(scrollPos, 10);
      });
    }
  }, []);

  const handleScroll = (e: any) => {
    sessionStorage.setItem('sidebarScrollPos', e.target.scrollTop);
  };

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { lg: 0 } }}
      className="sidebar-main-div"
    >
      <Drawer
        className="sidebar_drawer"
        variant="temporary"
        open={mobileOpen}
        onClose={handleSidebarToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          onScroll: handleScroll,
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
            right: 0,
          },
        }}
      >
        <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
          <figure>
            <img
              src={Logo}
              alt=""
              onClick={handleNavigate}
            />
          </figure>

          <IconButton
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              color: "white",
              display: { lg: "none" },
            }}
            onClick={handleSidebarToggle}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        {list?.length
          ? list
            .filter((ele: any) => Boolean(ele))
            .map((item: any) => {
              const isSelected = location.pathname.includes(item?.selected);
              return (
                <List key={item?.key} className="sidebr-lst">
                  <ListItemButton
                    className={`lst-itm ${isSelected ? "Mui-selected" : ""}`}
                    onClick={() => navigate(item?.selected)}
                  >
                    <ListItemIcon>{item?.icon}</ListItemIcon>
                    <ListItemText
                      className="lstitm-txt"
                      primary={item?.name}
                    />
                  </ListItemButton>
                </List>
              );
            })
          : null}
      </Drawer>
      <Drawer
        variant="permanent"
        className="sidebar_drawer"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
            right: 0,
          },
        }}
        PaperProps={{
          onScroll: handleScroll,
        }}
        open
      >
        <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
          <figure>
            <img
              src={Logo}
              alt=""
              onClick={handleNavigate}
            />
          </figure>
          <IconButton
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              color: "white",
              display: { lg: "none" },
            }}
            onClick={handleSidebarToggle}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
        {list?.length
          ? list
            .filter((ele: any) => Boolean(ele))
            .map((item: any) => {
              const isSelected = location.pathname.includes(item?.selected);
              return (
                <List key={item?.key} className="sidebr-lst">
                  <ListItemButton
                    className={`lst-itm ${isSelected ? "Mui-selected" : ""}`}
                    // className="lst-itm"
                    // selected={
                    //   location.pathname.includes(item.selected) === item.selected
                    // }
                    onClick={() => navigate(item?.selected)}
                  >
                    <ListItemIcon>{item?.icon}</ListItemIcon>
                    <ListItemText
                      className="lstitm-txt"
                      primary={item?.name}
                    />
                  </ListItemButton>
                </List>
              );
            })
          : null}
        {/* {drawer} */}
      </Drawer>
    </Box>
  );
}
